import {
  cloneElement,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

interface TooltipProps {
  label: ReactNode;
  children: ReactElement;
  side?: 'top' | 'bottom';
  // Required when `label` is not a plain string — used for the trigger's aria-label.
  ariaLabel?: string;
}

const VIEWPORT_PADDING = 8;

export function Tooltip({ label, children, side = 'bottom', ariaLabel }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [shiftX, setShiftX] = useState(0);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);
  const openedByTap = useRef(false);
  const id = useId();

  const close = () => {
    setOpen(false);
    openedByTap.current = false;
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => close();
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    if (!openedByTap.current) {
      return () =>
        window.removeEventListener('scroll', onScroll, { capture: true });
    }
    const onDoc = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onDoc);
    return () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      document.removeEventListener('pointerdown', onDoc);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setShiftX(0);
      return;
    }
    const tip = tooltipRef.current;
    const wrap = wrapRef.current;
    if (!tip || !wrap) return;
    const adjust = () => {
      const tipRect = tip.getBoundingClientRect();
      const wrapRect = wrap.getBoundingClientRect();
      const vw = window.innerWidth;
      const triggerCenter = wrapRect.left + wrapRect.width / 2;
      const half = tipRect.width / 2;
      const minCenter = VIEWPORT_PADDING + half;
      const maxCenter = vw - VIEWPORT_PADDING - half;
      const desiredCenter = Math.min(Math.max(triggerCenter, minCenter), maxCenter);
      setShiftX(desiredCenter - triggerCenter);
    };
    adjust();
    window.addEventListener('resize', adjust);
    return () => window.removeEventListener('resize', adjust);
  }, [open, label]);

  if (!isValidElement(children)) return children;

  const isStringLabel = typeof label === 'string';
  const triggerAriaLabel = isStringLabel ? (label as string) : ariaLabel;

  const trigger = cloneElement(children as ReactElement<Record<string, unknown>>, {
    'aria-label': triggerAriaLabel,
    'aria-describedby': open ? id : undefined,
    onPointerEnter: (e: ReactPointerEvent) => {
      if (e.pointerType === 'mouse') setOpen(true);
    },
    onPointerLeave: (e: ReactPointerEvent) => {
      if (e.pointerType === 'mouse') close();
    },
    onFocus: () => setOpen(true),
    onBlur: close,
    onClick: () => {
      setOpen(true);
      openedByTap.current = true;
    },
  });

  const positionCls =
    side === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2';

  return (
    <span ref={wrapRef} className="relative inline-flex">
      {trigger}
      <span
        ref={tooltipRef}
        role="tooltip"
        id={id}
        data-state={open ? 'open' : 'closed'}
        style={{ transform: `translateX(calc(-50% + ${shiftX}px))` }}
        className={`pointer-events-none absolute left-1/2 z-20 max-w-[calc(100vw-16px)] whitespace-nowrap rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-900 shadow-lg transition-opacity duration-100 data-[state=closed]:opacity-0 data-[state=open]:opacity-100 ${positionCls}`}
      >
        {label}
      </span>
    </span>
  );
}
